/**
 * D434: the Map quality setting, Low, Standard or High. A player preference kept per device in
 * localStorage beside the flat-map one, Standard by default (a missing or unreadable value is
 * Standard). The 3D map's own module takes it as `quality`: the map lane wires that after its
 * speed work, and the automatic drop under load stays beneath this choice (D421). Nothing here
 * reads the map, and the map does not read this yet.
 */
import { useEffect, useState } from 'preact/hooks';

export type MapQuality = 'low' | 'standard' | 'high';

export const MAP_QUALITY_KEY = 'hedgerow-map-quality';
export const MAP_QUALITIES: readonly MapQuality[] = ['low', 'standard', 'high'];

/** The words the setting shows. */
export const MAP_QUALITY_LABEL = 'Map quality';
export const MAP_QUALITY_WORDS: Record<MapQuality, string> = {
  low: 'Low',
  standard: 'Standard',
  high: 'High',
};

function read(): MapQuality {
  try {
    const v = globalThis.localStorage?.getItem(MAP_QUALITY_KEY);
    return v === 'low' || v === 'high' ? v : 'standard';
  } catch {
    return 'standard';
  }
}

let current: MapQuality = read();
const listeners = new Set<(q: MapQuality) => void>();

/** The player's choice: `'low'`, `'standard'` (the default) or `'high'`. */
export function getMapQuality(): MapQuality {
  return current;
}

export function setMapQuality(value: MapQuality): void {
  current = value;
  try {
    globalThis.localStorage?.setItem(MAP_QUALITY_KEY, value);
  } catch {
    // Kept for this session only.
  }
  for (const fn of listeners) fn(value);
}

/** The settings row: `[quality, set]`. */
export function useMapQuality(): [MapQuality, (q: MapQuality) => void] {
  const [value, setValue] = useState(current);
  useEffect(() => {
    listeners.add(setValue);
    return () => void listeners.delete(setValue);
  }, []);
  return [value, setMapQuality];
}
